import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchEntryBySlug, fetchCollection } from '../lib/cms';
import ScrollReveal from '../components/ScrollReveal';

export default function Profil() {
  const [pageData, setPageData] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [profileItems, allGalleries] = await Promise.all([
          fetchCollection('profile_page'),
          fetchCollection('galleries')
        ]);
        const page = (Array.isArray(profileItems) && profileItems[0]) || null;
        setPageData(page);
        setGalleries(allGalleries || []);
      } catch (err) {
        console.error('Error loading profil data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title?.da || pageData?.meta_title || "Om SKT Byg | Erfaren tømrer & murer siden 2007 | Galten";
  const metaDesc = pageData?.meta_description?.da || pageData?.meta_description || "Mød SKT Byg i Galten. Grundlagt i 2007 med fokus på solidt håndværk, overholdte aftaler og tæt kundedialog. Læs om vores historie og erfarne håndværkerteam.";

  const pageTitle = pageData?.page_title?.da || pageData?.page_title || "Profil";
  const pageSubtitle = pageData?.page_subtitle?.da || pageData?.page_subtitle || "God service og godt håndværk er vores fundament. Vi leverer altid professionelle resultater til aftalt tid.";
  const historyTitle = pageData?.history_title?.da || pageData?.history_title || "Etableret i 2007";
  const historyImg = (pageData?.history_image && pageData.history_image[0]) || "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/skt-byg-hjemmeside-201-medium.jpg";
  const teamTitle = pageData?.team_title?.da || pageData?.team_title || "Et stærkt mandskab med bred faglighed";
  const teamBenefitTitle = pageData?.team_benefit_title?.da || pageData?.team_benefit_title || "Hvad betyder vores størrelse for dig som kunde?";
  const teamBenefitText = pageData?.team_benefit_text?.da || pageData?.team_benefit_text || "Det betyder, at vi har mandskabet og certificeringerne (herunder VELUX og Junckers Gulvlægger) til at håndtere store nybyggerier, akutte forsikringsskader og specialopgaver hurtigt og med præcis online timeregistrering – samtidig med at du altid har direkte dialog med tømrermesteren.";
  const sponsorshipTitle = pageData?.sponsorship_title?.da || pageData?.sponsorship_title || "Vi støtter og sponsorerer det lokale liv";
  const galleryTitle = pageData?.gallery_title?.da || pageData?.gallery_title || "Vores referencegalleri";
  const galleryText = pageData?.gallery_text?.da || pageData?.gallery_text || "Gå på opdagelse i et udvalg af vores tidligere projekter. Vi sætter en ære i det gode håndværk og leverer altid løsninger i højeste kvalitet.";

  return (
    <div className="min-h-screen bg-brand-light py-16 overflow-x-hidden">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/skt-byg-hjemmeside-201-medium.jpg" />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Sidetitel */}
        <ScrollReveal className="max-w-3xl mb-16" direction="up">
          <h1 className="text-4xl md:text-5xl font-extrabold text-brand-dark mb-4">
            {pageTitle}
          </h1>
          <p className="text-lg text-brand-dark/70 leading-relaxed">
            {pageSubtitle}
          </p>
        </ScrollReveal>

        {/* Historie & Teambillede */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-24">
          <ScrollReveal className="lg:col-span-7 space-y-6" direction="left">
            <h2 className="text-2xl md:text-3xl font-extrabold text-brand-dark">
              {historyTitle}
            </h2>
            {pageData?.history_text?.da ? (
              <div className="text-brand-dark/80 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: pageData.history_text.da }} />
            ) : (
              <>
                <p className="text-brand-dark/80 leading-relaxed">
                  SKT-Byg er en etableret tømrervirksomhed med en solid medarbejderstab, som blev etableret af tømrermester Steffen K. Thomsen i 2007. Virksomheden er beliggende i Galten området, og ud over opgaver i lokalområdet udfører vi projekter i hele det midt- og østjyske.
                </p>
                <p className="text-brand-dark/80 leading-relaxed">
                  Steffen Thomsen, der driver firmaet, har været tømrer siden 1998 og selvstændig siden 2007. Med stor faglig stolthed og et stærkt hold af dygtige håndværkere løser vi alt fra mindre renoveringer til store totalløsninger.
                </p>
              </>
            )}
          </ScrollReveal>
          <ScrollReveal className="lg:col-span-5" direction="right">
            <div className="overflow-hidden shadow-md border border-brand-sand">
              <img
                src={historyImg}
                alt="SKT Byg Team"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </ScrollReveal>
        </div>

        {/* Team & Kapacitet sektion */}
        <div className="mb-24 bg-white border border-brand-sand p-8 md:p-12 shadow-sm">
          <ScrollReveal className="max-w-3xl mb-10" direction="up">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark mb-4">
              {teamTitle}
            </h2>
            {pageData?.team_text?.da ? (
              <div className="text-brand-dark/80 text-base md:text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: pageData.team_text.da }} />
            ) : (
              <p className="text-brand-dark/80 text-base md:text-lg leading-relaxed">
                Hos SKT Byg er vi ikke bare en enkelt mand i en varevogn. Vi er i dag et velkoordineret team på <strong>7 mand</strong>, hvilket giver os den nødvendige kapacitet og fleksibilitet til at løfte både store, omfattende byggeprojekter og mindre specialopgaver effektivt og til aftalt tid.
              </p>
            )}
          </ScrollReveal>

          {/* Team oversigt kort */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <ScrollReveal delay={50} direction="up" className="bg-brand-light p-6 border-l-4 border-brand-accent flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-brand-dark text-white flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-hammer"></i>
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">Erfarne Tømrere</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed">
                  Vores svende – herunder Thomas, Rasmus og Klaus – sikrer solidt håndværk, præcision og højeste finish i trækonstruktioner, tagarbejde, glarmesteropgaver og montering.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-sand/80 text-xs font-bold text-brand-accent">
                Faguddannede tømrersvende & glarmester
              </div>
            </ScrollReveal>

            <ScrollReveal delay={150} direction="up" className="bg-brand-light p-6 border-l-4 border-brand-dark flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-brand-dark text-white flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-trowel-bricks"></i>
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">Egen Murerfaglighed</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed">
                  Med murer Magnus i teamet klarer vi mureropgaver, sokler, badeværelser og pudsearbejde internt uden fordyrende eller forsinkende mellemled.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-sand/80 text-xs font-bold text-brand-dark">
                Komplette fagløsninger
              </div>
            </ScrollReveal>

            <ScrollReveal delay={250} direction="up" className="bg-brand-light p-6 border-l-4 border-brand-accent flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-brand-dark text-white flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-graduation-cap"></i>
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">2 Lærlinge</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed">
                  Vi tager ansvar for fagets fremtid. Vores 2 lærlinge oplæres grundigt i traditionelt håndværk, kvalitet og god kundeservice under tæt mesterlære.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-sand/80 text-xs font-bold text-brand-accent">
                Uddannelse & fremtid
              </div>
            </ScrollReveal>

            <ScrollReveal delay={350} direction="up" className="bg-brand-light p-6 border-l-4 border-brand-dark flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-brand-dark text-white flex items-center justify-center text-xl mb-4">
                  <i className="fa-solid fa-clipboard-check"></i>
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">Mesterstyring</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed">
                  Tømrermester Steffen Thomsen følger projekterne tæt fra start til slut, koordinerer mandskabet og sikrer, at alle aftaler og tidsplaner overholdes.
                </p>
              </div>
              <div className="mt-4 pt-4 border-t border-brand-sand/80 text-xs font-bold text-brand-dark">
                Direkte kontakt & overblik
              </div>
            </ScrollReveal>
          </div>

          {/* Ekstra fordelstekst om kapaciteten */}
          <div className="bg-brand-sand/40 border border-brand-sand p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2">
              <h4 className="text-lg font-bold text-brand-dark flex items-center gap-2">
                <i className="fa-solid fa-circle-check text-brand-accent"></i>
                <span>{teamBenefitTitle}</span>
              </h4>
              <p className="text-sm text-brand-dark/80 leading-relaxed max-w-3xl">
                {teamBenefitText}
              </p>
            </div>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3 bg-brand-accent hover:bg-brand-accent-hover text-white font-bold text-sm transition-all duration-300 gap-2 shrink-0 hover:scale-105"
            >
              <span>Tag en snak med os</span>
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </div>
        </div>

        {/* Støtte til lokalsamfundet og sponsorater */}
        <div className="mb-24">
          <ScrollReveal direction="up" className="bg-brand-dark text-white p-8 md:p-12 relative overflow-hidden border-t-4 border-brand-accent shadow-md">
            {/* Diskret dekorativ baggrundsikon */}
            <div className="absolute right-6 -bottom-8 text-white/5 text-9xl pointer-events-none select-none">
              <i className="fa-solid fa-hands-holding-child"></i>
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <h2 className="text-2xl md:text-3xl font-extrabold text-white">
                  {sponsorshipTitle}
                </h2>
                {pageData?.sponsorship_text?.da ? (
                  <div className="text-white/80 leading-relaxed text-base md:text-lg space-y-4" dangerouslySetInnerHTML={{ __html: pageData.sponsorship_text.da }} />
                ) : (
                  <>
                    <p className="text-white/80 leading-relaxed text-base md:text-lg">
                      Hos SKT Byg er vi stolte af vores rødder i Galten, Storring, Stjær og de omkringliggende byer. For os er et stærkt lokalsamfund fundamentet for en sund udvikling og gode fællesskaber.
                    </p>
                    <p className="text-white/70 leading-relaxed text-sm md:text-base">
                      Derfor vægter vi det højt at give noget tilbage ved løbende at sponsorere og bakke op om lokale foreninger, idrætsklubber og fælles aktiviteter i lokalområdet. Når vi støtter det lokale, er vi med til at styrke rammerne for både børn, unge og voksne.
                    </p>
                  </>
                )}
              </div>

              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4">
                <div className="bg-white/10 border border-white/15 p-5 flex items-start gap-4 backdrop-blur-sm">
                  <div className="w-10 h-10 rounded-none bg-brand-accent text-white flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-trophy text-base"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm mb-1">Lokale idrætsforeninger</h3>
                    <p className="text-xs text-white/70">Opbakning til sport, bevægelse og sunde fællesskaber for alle aldre.</p>
                  </div>
                </div>

                <div className="bg-white/10 border border-white/15 p-5 flex items-start gap-4 backdrop-blur-sm">
                  <div className="w-10 h-10 rounded-none bg-brand-accent text-white flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-people-group text-base"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm mb-1">Lokale arrangementer & tiltag</h3>
                    <p className="text-xs text-white/70">Støtte til aktiviteter, der samler og styrker vores nærområde.</p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>

        {/* Galleri-sektion */}
        <div className="border-t border-brand-sand pt-16">
          <ScrollReveal className="max-w-3xl mb-12" direction="up">
            <h2 className="text-3xl font-extrabold text-brand-dark mb-4">
              {galleryTitle}
            </h2>
            <p className="text-brand-dark/70 leading-relaxed">
              {galleryText}
            </p>
          </ScrollReveal>

          {loading ? (
            <div className="flex justify-center py-12">
              <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-accent"></i>
            </div>
          ) : galleries.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {galleries.slice(0, 4).map((gallery, idx) => (
                <ScrollReveal key={gallery.id} delay={idx * 100} direction="up">
                  <Link
                    to={`/referencegalleri/${gallery.slug.da}`}
                    className="group bg-white border border-brand-sand overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col cursor-pointer h-full"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={gallery.images?.[0] ? `https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/${gallery.images[0].replace('https://assets.auxodev.dk/', '')}` : "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/no-image.png"}
                        alt={gallery.title.da}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-300"></div>
                    </div>
                    <div className="p-6 flex-grow flex flex-col justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-brand-dark group-hover:text-brand-accent transition-colors duration-300 mb-2">
                          {gallery.title.da}
                        </h3>
                        {gallery.description?.da && (
                          <div 
                            className="text-sm text-brand-dark/60 line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: gallery.description.da }}
                          />
                        )}
                      </div>
                      <span className="text-xs font-bold text-brand-accent flex items-center gap-1.5 mt-4">
                        Se billeder <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform duration-300"></i>
                      </span>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white border border-brand-sand">
              <p className="text-brand-dark/50 font-semibold">Der er i øjeblikket ingen gallerier tilgængelige.</p>
            </div>
          )}

          <ScrollReveal className="text-center" direction="up" delay={200}>
            <Link
              to="/referencegalleri"
              className="inline-flex items-center justify-center px-6 py-3 border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white font-bold transition-all duration-300 gap-2 hover:scale-105"
            >
              <span>Se alle gallerier</span>
              <i className="fa-solid fa-images"></i>
            </Link>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
}
